import { useEffect, useRef, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";

import { Button } from "@/components/Button";
import { MeshField } from "@/components/MeshField";
import { useDocumentTitle } from "@/lib/useDocumentTitle";

import styles from "./RouteFallbacks.module.css";

/**
 * The dead end the codebase already has (`CaseGone`, UX gate round 4, #11):
 * the state under its own name, the focus on it — the screen that was
 * expected is not there, and the person should hear why.
 *
 * Beside the sign-in's mesh field (UX-Gutachten 1, B-20): a large empty
 * card with small text was all the app showed at its edges, and its
 * strongest picture stood only at the door. The field is the card's left
 * third — K60's anthracite with the grating at 22°, still (no `settle`: this
 * is no entrance) — and the words and the ways on stand beside it; on a
 * phone the field is a band above them. Decoration, `aria-hidden`.
 */
function DeadEnd({ title, actions, children }: { title: string; actions: ReactNode; children: ReactNode }) {
  const anchor = useRef<HTMLDivElement>(null);
  useEffect(() => {
    anchor.current?.focus();
  }, []);
  return (
    <div ref={anchor} tabIndex={-1} role="group" aria-label={title} className={styles.anchor}>
      <div className={styles.card}>
        <div className={styles.steel} aria-hidden="true">
          <MeshField className={styles.mesh} />
        </div>
        <div className={styles.text}>
          {/* A dead end is a place too (UX-Gutachten 2, B-13): its title in
              the places' one rule, behind the screen's one red marker. */}
          <p className={styles.title}>
            <span className="marke marke-titel" aria-hidden="true" />
            {title}
          </p>
          <div className={styles.body}>{children}</div>
          <div className={styles.actions}>{actions}</div>
        </div>
      </div>
    </div>
  );
}

/**
 * An address that leads to no screen (Welle 0, S4, 18.10.2026). It showed an
 * empty work area under the masthead — nothing said, nothing to do.
 */
export function NotFoundScreen() {
  useDocumentTitle("Seite nicht gefunden");
  const navigate = useNavigate();
  return (
    <DeadEnd
      title="Diese Seite gibt es nicht"
      actions={
        <Button variant="primary" onClick={() => navigate("/")}>
          Zur Startseite
        </Button>
      }
    >
      Die Adresse führt zu keinem Schirm der Werkbank. Deine Vorgänge findest du auf der Startseite.
    </DeadEnd>
  );
}

/**
 * A screen that failed to render (Welle 0, S4). Under the shell, so the
 * masthead stays and Start and Vorgänge are one click away. Both ways on load the page
 * anew: whatever broke this screen lives in this tab's memory, and only a
 * fresh load is sure to leave it behind. What is stored stays stored — every
 * Stand lives on the server.
 */
export function RouteErrorScreen() {
  useDocumentTitle("Fehler");
  return (
    <DeadEnd
      title="Hier ist etwas schiefgelaufen"
      actions={
        <>
          <Button variant="primary" onClick={() => window.location.reload()}>
            Seite neu laden
          </Button>
          <Button onClick={() => window.location.assign("/")}>Zur Startseite</Button>
        </>
      }
    >
      Dieser Schirm ließ sich nicht anzeigen. Gespeicherte Stände sind davon nicht betroffen.
    </DeadEnd>
  );
}
